import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { DRIVE_KINDS, driveCapacity, type DriveBoard, type DriveBoardRow, type DriveBoardProject } from '../../shared/drives';
import { PHASE_LABELS, PROJECT_PHASES, type ProjectPhase } from '../../shared/types';
import { api } from '../lib/api';
import { useIsMobile } from '../lib/useIsMobile';
import { Dropdown, Modal, Seg, formatProjectNumber, placeMenu, type Placement } from './primitives';

export type DriveSelection = { drive: DriveBoardRow; project: DriveBoardProject };
type BulkMode = 'move' | 'add';
type BoardFilter = 'all' | 'single' | 'phase';
const roleMark = { primary: 'P', backup: 'B', archive: 'A' } as const;
const selectionKey = (drive: string, project: string) => `${drive}:${project}`;
const projectsLabel = (n: number) => `${n} project${n === 1 ? '' : 's'}`;

export function filterBoardProjects(projects: DriveBoardProject[], filter: BoardFilter, phases: ProjectPhase[], search: string) {
  const query = search.trim().toLowerCase().replace(/^#/, '');
  return projects.filter(p => (filter !== 'single' || p.link_count === 1)
    && (filter !== 'phase' || !phases.length || phases.includes(p.phase))
    && (!query || p.name.toLowerCase().includes(query) || (formatProjectNumber(p.project_number) ?? '').toLowerCase().includes(query)));
}

function firstSourceSelections(selection: DriveSelection[]) {
  // A project can be checked on several columns, but one target can hold only
  // one link. Use its first selected source; leave the other links intact.
  const seen = new Set<string>();
  return selection.filter(item => {
    if (seen.has(item.project.project_id)) return false;
    seen.add(item.project.project_id); return true;
  });
}

export function planDriveRelink(selection: DriveSelection[], target: DriveBoardRow, mode: BulkMode) {
  const unique = firstSourceSelections(selection);
  const targetIds = new Set(target.projects.map(p => p.project_id));
  const rows = unique.map(item => ({ ...item, skipped: targetIds.has(item.project.project_id) }));
  const sources = [...new Map(rows.map(row => [row.drive.id, row.drive])).values()];
  const skipped = rows.filter(row => row.skipped).length;
  const count = rows.length - skipped;
  const from = sources.length === 1 ? sources[0].label : `${sources.length} drives`;
  const archives = rows.filter(row => !row.skipped && row.project.has_backup).length;
  const skipSentence = !skipped ? '' : skipped === 1
    ? ` 1 project is already on ${target.label} and will be skipped.`
    : ` ${projectsLabel(skipped)} already on ${target.label} will be skipped.`;
  const sentence = (mode === 'move'
    ? `Move ${projectsLabel(rows.length)} from ${from} to ${target.label}. Roles are kept.`
    : `Add a backup link on ${target.label} for ${projectsLabel(rows.length)}.`
      + (archives ? ` ${archives} will be added as archive because they already have a backup.` : '')) + skipSentence;
  const singleCopyNames = rows.filter(row => !row.skipped && row.project.link_count === 1)
    .map(row => formatProjectNumber(row.project.project_number) ?? row.project.name);
  const listedNames = singleCopyNames.slice(0, 5);
  if (singleCopyNames.length > 5) listedNames.push(`${singleCopyNames.length - 5} more`);
  const singleCopyWarning = singleCopyNames.length
    ? `${listedNames.length === 1 ? listedNames[0] : `${listedNames.slice(0, -1).join(', ')} and ${listedNames.at(-1)}`} ${singleCopyNames.length === 1 ? 'stays' : 'stay'} single copy after the move. Add a backup on another drive to clear the warning.`
    : '';
  const actionLabel = mode === 'move' ? `Move ${projectsLabel(count)}` : `Add ${count} backup${count === 1 ? '' : 's'}`;
  const requests = sources.filter(source => rows.some(row => row.drive.id === source.id && !row.skipped))
    .map(source => ({ project_ids: rows.filter(row => row.drive.id === source.id).map(row => row.project.project_id),
      from_drive_id: source.id, to_drive_id: target.id, mode }));
  return { rows, sources, count, sentence, singleCopyWarning, actionLabel, requests, duplicateCount: selection.length - unique.length };
}

function PhaseMenu({ anchorRef, phases, selected, onChange, onClose }: {
  anchorRef: RefObject<HTMLDivElement>; phases: ProjectPhase[]; selected: ProjectPhase[];
  onChange: (phases: ProjectPhase[]) => void; onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Placement | null>(null);
  useLayoutEffect(() => {
    const anchor = anchorRef.current?.querySelector('button:last-child');
    const place = () => {
      if (!anchor || !ref.current) return;
      setPosition(placeMenu({ anchor: anchor.getBoundingClientRect(), height: ref.current.offsetHeight,
        width: 220, viewportWidth: window.innerWidth, viewportHeight: window.innerHeight }));
    };
    place(); window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); (anchor as HTMLElement | undefined)?.focus(); };
  }, [anchorRef]);
  const placed = position !== null;
  useLayoutEffect(() => {
    if (placed) ref.current?.querySelector<HTMLElement>('input, button')?.focus();
  }, [placed]);
  useEffect(() => {
    const outside = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node) && !anchorRef.current?.contains(e.target as Node)) onClose(); };
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('pointerdown', outside); document.addEventListener('keydown', escape, true);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape, true); };
  }, [anchorRef, onClose]);
  return createPortal(<div ref={ref} className="dropdown drive-phase-menu" role="group" aria-label="Project phases"
    style={{ position: 'fixed', top: position?.top, bottom: position?.bottom, left: position?.left, visibility: position ? 'visible' : 'hidden' }}>
    {phases.map(phase => <label className="dd-item" key={phase}><input type="checkbox" checked={selected.includes(phase)}
      onChange={() => onChange(selected.includes(phase) ? selected.filter(p => p !== phase) : [...selected, phase])} />{PHASE_LABELS[phase]}</label>)}
    <div className="dd-sep" /><button className="dd-item" onClick={() => { onChange([]); onClose(); }}>Any phase</button>
  </div>, document.body);
}

function DriveTarget({ mode, drives, onChoose, disabled }: {
  mode: BulkMode; drives: DriveBoardRow[]; onChoose: (drive: DriveBoardRow) => void; disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const label = mode === 'move' ? 'Move to' : 'Add backup on';
  return <div className="drive-board-target"><span className="lbl">{label}</span><div className="dropdown-anchor">
    <button type="button" className={`drive-target-select${open ? ' on' : ''}`} aria-label={label} aria-haspopup="listbox" aria-expanded={open}
      disabled={disabled || !drives.length} onClick={() => setOpen(v => !v)}><span>{drives.length ? 'Choose drive' : 'No other drives'}</span><span className="caret">▾</span></button>
    {open && <Dropdown searchable label={label} items={drives.map(d => ({ value: d.id, label: d.label }))} onClose={() => setOpen(false)}
      onSelect={id => { setOpen(false); onChoose(drives.find(d => d.id === id)!); }} />}
  </div></div>;
}

export function DriveProjectChip({ project: p, drive, selected, toggle }: {
  project: DriveBoardProject; drive: DriveBoardRow; selected: boolean; toggle: () => void;
}) {
  return <div className={`drive-project-chip ${p.link_count === 1 ? 'single' : 'calm'}${selected ? ' selected' : ''}`} onClick={toggle}
    title={`${formatProjectNumber(p.project_number)} ${p.name} · ${p.role} · ${p.link_count === 1 ? 'single copy' : `on ${p.link_count} drives`}`}>
    <input type="checkbox" aria-label={`Select ${p.name} on ${drive.label}`} checked={selected} onClick={e => e.stopPropagation()} onChange={toggle} />
    <Link className="no" to={`/projects/${p.project_id}`} onClick={e => e.stopPropagation()} aria-label={`Open ${p.name}`}>{formatProjectNumber(p.project_number) ?? '↗'}</Link>
    <span className="nm">{p.name}</span><span className={`role ${p.role}`} aria-label={p.role}>{roleMark[p.role]}</span>
  </div>;
}

export function DrivesBoard({ onChanged }: { onChanged?: () => void } = {}) {
  const [data, setData] = useState<DriveBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState<BoardFilter>('all');
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const [search, setSearch] = useState('');
  const [phaseOpen, setPhaseOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [confirm, setConfirm] = useState<{ target: DriveBoardRow; mode: BulkMode } | null>(null);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const phaseAnchor = useRef<HTMLDivElement>(null);
  const mobile = useIsMobile();
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api<DriveBoard>('/api/admin/drives/board')); }
    catch (e) { setData(null); throw e; }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load().catch(e => setError((e as Error).message)); }, [load]);
  useEffect(() => {
    if (!mobile || !selected.length) return;
    document.body.setAttribute('data-bulk-open', '');
    return () => document.body.removeAttribute('data-bulk-open');
  }, [mobile, selected.length]);
  const drives = data?.drives ?? [];
  const links = new Map(drives.flatMap(drive => drive.projects.map(project => [selectionKey(drive.id, project.project_id), { drive, project }] as const)));
  const selection = selected.flatMap(key => links.has(key) ? [links.get(key)!] : []);
  const sourceIds = new Set(firstSourceSelections(selection).map(item => item.drive.id));
  const presentPhases = PROJECT_PHASES.filter(phase => drives.some(d => d.projects.some(p => p.phase === phase)));
  const plan = confirm ? planDriveRelink(selection, confirm.target, confirm.mode) : null;
  const toggle = (drive: string, project: string) => {
    if (busy.current) return;
    const key = selectionKey(drive, project);
    setSelected(keys => keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key]);
    setSuccess('');
  };
  const submit = async () => {
    if (!confirm || !plan?.count || busy.current) return;
    busy.current = true; setSaving(true); setError(''); setSuccess('');
    let changed = 0;
    const errors: string[] = [];
    // Requests are sequential: each API call accepts one source drive. Always
    // refresh after the batch, including partial failures, to avoid stale retries.
    for (const request of plan.requests) {
      try {
        const result = await api<{ moved?: number; added?: number }>('/api/admin/drives/bulk-relink', { method: 'POST', json: request });
        changed += result.moved ?? result.added ?? 0;
      } catch (e) { errors.push((e as Error).message); }
    }
    try { await load(); } catch (e) { errors.push(`Could not refresh board: ${(e as Error).message}`); }
    if (changed > 0) setSelected([]);
    setConfirm(null);
    if (changed) { setSuccess(confirm.mode === 'move' ? `Moved ${projectsLabel(changed)}` : `Added ${changed} backup${changed === 1 ? '' : 's'}`); onChanged?.(); }
    setError([...new Set(errors)].join(' ')); setSaving(false); busy.current = false;
  };
  const visibleCount = drives.reduce((n, d) => n + filterBoardProjects(d.projects, filter, phases, search).length, 0);
  return <div className={`drives-board${selection.length ? ' has-selection' : ''}`} aria-label="Drives board">
    <div className="drive-board-bar">
      <div ref={phaseAnchor}><Seg value={filter} options={[{ value: 'all', label: 'All' }, { value: 'single', label: 'Single copy only' },
        { value: 'phase', label: `${phases.length ? `Phase: ${phases.map(p => PHASE_LABELS[p]).join(', ')}` : 'Phase'} ▾` }]}
        onChange={next => { if (next === 'phase') setPhaseOpen(v => !v); else { setPhaseOpen(false); setFilter(next); setPhases([]); } }} />
        {phaseOpen && <PhaseMenu anchorRef={phaseAnchor} phases={presentPhases} selected={phases} onClose={() => setPhaseOpen(false)}
          onChange={next => { setPhases(next); setFilter(next.length ? 'phase' : 'all'); }} />}
      </div>
      <input className="f-input drive-board-search" type="search" aria-label="Search projects on drives" placeholder="Search number or name…" value={search} onChange={e => setSearch(e.target.value)} />
      <div className="drive-board-legend"><span><i className="sw" />Backed up · on 2+ drives</span><span><i className="sw single" />Single copy · on one drive only</span></div>
    </div>
    {loading && <p className="reg-sub" role="status">Loading board…</p>}
    {error && <p className="drive-error" role="alert">{error} {!data && <button className="btn" disabled={saving} onClick={() => { setError(''); void load().catch(e => setError((e as Error).message)); }}>Retry</button>}</p>}
    {success && <p className="drive-board-success" role="status">{success}</p>}
    {!!selection.length && <div className="drive-board-actions" role="toolbar" aria-label="Selected projects">
      <span className="bulk-count">{selection.length} selected</span><span className="sep">·</span>
      <DriveTarget mode="move" drives={drives.filter(d => !sourceIds.has(d.id))} disabled={saving} onChoose={target => setConfirm({ target, mode: 'move' })} />
      <span className="sep">·</span><DriveTarget mode="add" drives={drives} disabled={saving} onChoose={target => setConfirm({ target, mode: 'add' })} />
      <button className="bulk-clear" disabled={saving} onClick={() => setSelected([])}>Clear</button>
    </div>}
    {data && <>
      {!drives.length && <p className="reg-sub">No active drives. Add a drive to start linking projects.</p>}
      {!!drives.length && !visibleCount && <p className="reg-sub">No projects match these filters.</p>}
      <div className="drive-board-cols">{drives.map(drive => <section className="drive-board-col" key={drive.id} aria-label={drive.label}>
        <div className="drive-board-col-head"><h3 title={drive.label}>{drive.label}</h3><div className="meta"><span className="kind">{DRIVE_KINDS.find(k => k.value === drive.kind)?.label}</span>
          <span>{driveCapacity(drive.capacity_gb)}</span><span className="n">{projectsLabel(drive.projects.length)}</span></div></div>
        <div className="drive-board-col-body">{filterBoardProjects(drive.projects, filter, phases, search).map(project => <DriveProjectChip key={project.project_id} project={project} drive={drive}
          selected={selected.includes(selectionKey(drive.id, project.project_id))} toggle={() => toggle(drive.id, project.project_id)} />)}</div>
      </section>)}</div>
      <p className="drive-board-foot"><span className="mono">{data.unlinked_count}</span> {data.unlinked_count === 1 ? 'project' : 'projects'} with no drive</p>
    </>}
    {confirm && plan && <Modal title={plan.actionLabel}
      context="Drives · board" width={540} onClose={() => { if (!busy.current) setConfirm(null); }} hint="Register only · no files are copied"
      foot={<><button className="btn" disabled={saving} onClick={() => setConfirm(null)}>Cancel</button><button className="btn primary" disabled={saving || !plan.count} onClick={submit}>
        {saving ? 'Saving…' : plan.actionLabel}</button></>}>
      <p className="drive-move-lead">{plan.sentence}</p>
      {plan.sources.length > 1 && <p className="reg-sub">Sources: {plan.sources.map(d => d.label).join(', ')}</p>}
      {!!plan.duplicateCount && <p className="reg-sub">Projects selected on multiple drives are counted once, using the first selected source shown below. Other links are kept.</p>}
      <div className="drive-move-list">{plan.rows.map(({ drive, project, skipped }) => <div className={`drive-move-row${skipped ? ' skip' : ''}`} key={project.project_id}>
        <span className="no">{formatProjectNumber(project.project_number)}</span><span className="nm" title={project.name}>{project.name}</span>
        <span className="to">{skipped ? `Skipped · already on ${confirm.target.label}` : `${drive.label} → ${confirm.target.label} · ${confirm.mode === 'move' ? roleMark[project.role] : project.has_backup ? 'A' : 'B'}`}</span>
      </div>)}</div>
      {confirm.mode === 'move' && plan.singleCopyWarning && <p className="drive-move-warn"><i className="sw single" />
        {plan.singleCopyWarning}</p>}
      {confirm.mode === 'add' && <p className="reg-sub">Projects that already have a backup role receive an archive link on this drive.</p>}
    </Modal>}
  </div>;
}
